import React, { useMemo, useState } from 'react';
import { AssetClass } from '../types/assetClass';
import { Allocation } from '../types/simulation';

interface Props {
  assetClasses: AssetClass[];
  currentAllocation: Partial<Allocation>;
}

const RiskReturnHeatmap: React.FC<Props> = ({
  assetClasses,
  currentAllocation
}) => {
  // Constants for the heatmap
  const width = 800;
  const height = 600;
  const padding = { top: 40, right: 60, bottom: 60, left: 60 };
  const cellSize = {
    width: (width - padding.left - padding.right) / 20,
    height: (height - padding.top - padding.bottom) / 20
  };

  // Risk and return ranges
  const riskRange = {
    min: 0,
    max: 0.20, // 20% volatility
    steps: 20
  };

  const returnRange = {
    min: 0.02, // 2% return
    max: 0.14, // 14% return
    steps: 20
  };

  // Calculate current portfolio risk and return
  const currentPosition = useMemo(() => {
    let totalRisk = 0;
    let totalReturn = 0;
    let totalWeight = 0;

    Object.entries(currentAllocation).forEach(([asset, weight]) => {
      const assetClass = assetClasses.find(a => a.name.toLowerCase() === asset.toLowerCase());
      if (assetClass && weight) {
        totalRisk += (assetClass.volatility / 100) * weight;
        totalReturn += (assetClass.longTermReturn / 100) * weight;
        totalWeight += weight;
      }
    });

    // Normalize by total weight
    if (totalWeight > 0) {
      totalRisk = totalRisk / totalWeight;
      totalReturn = totalReturn / totalWeight;
    }

    return { risk: totalRisk, return: totalReturn };
  }, [assetClasses, currentAllocation]);

  // Generate heatmap data
  const heatmapData = useMemo(() => {
    const data: { x: number; y: number; value: number }[] = [];
    
    for (let i = 0; i < riskRange.steps; i++) {
      for (let j = 0; j < returnRange.steps; j++) {
        const risk = riskRange.min + (i * (riskRange.max - riskRange.min) / riskRange.steps);
        const ret = returnRange.min + (j * (returnRange.max - returnRange.min) / returnRange.steps);
        
        // Calculate desirability score (0 to 1)
        // Higher return and lower risk is better
        const riskScore = 1 - (risk / riskRange.max);
        const returnScore = (ret - returnRange.min) / (returnRange.max - returnRange.min);
        const sharpeRatio = (ret - 0.02) / risk; // Assuming 2% risk-free rate
        
        // Combine scores with weights
        const value = (returnScore * 0.4 + riskScore * 0.3 + Math.min(sharpeRatio, 1) * 0.3);
        
        data.push({ x: risk, y: ret, value });
      }
    }
    
    return data;
  }, [riskRange.max, riskRange.min, riskRange.steps, returnRange.max, returnRange.min, returnRange.steps]);

  // Scale functions
  const xScale = (x: number) => 
    ((x - riskRange.min) / (riskRange.max - riskRange.min)) * 
    (width - padding.left - padding.right) + padding.left;

  const yScale = (y: number) => 
    height - padding.bottom - 
    ((y - returnRange.min) / (returnRange.max - returnRange.min)) * 
    (height - padding.top - padding.bottom);

  // Color scale function
  const getColor = (value: number) => {
    // Red to blue gradient
    const r = Math.round(255 * (1 - value));
    const b = Math.round(255 * value);
    const g = Math.round(255 * (0.5 - Math.abs(0.5 - value)));
    return `rgb(${r},${g},${b})`;
  };

  // Generate table data for display
  const tableData = useMemo(() => {
    const data = [];
    for (let i = 0; i < riskRange.steps; i += 4) { // Show every 4th point for readability
      const risk = riskRange.min + (i * (riskRange.max - riskRange.min) / riskRange.steps);
      for (let j = 0; j < returnRange.steps; j += 4) {
        const ret = returnRange.min + (j * (returnRange.max - returnRange.min) / returnRange.steps);
        const riskScore = 1 - (risk / riskRange.max);
        const returnScore = (ret - returnRange.min) / (returnRange.max - returnRange.min);
        const sharpeRatio = (ret - 0.02) / risk;
        const score = (returnScore * 0.4 + riskScore * 0.3 + Math.min(sharpeRatio, 1) * 0.3);
        data.push({ risk, return: ret, score });
      }
    }
    return data;
  }, [riskRange.max, riskRange.min, riskRange.steps, returnRange.max, returnRange.min, returnRange.steps]);

  const [showTable, setShowTable] = useState(false);

  return (
    <div style={{ marginBottom: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
        <div>
          <h3>Risk/Return Heatmap Analysis</h3>
          <p style={{ color: '#666', marginBottom: '1rem', maxWidth: '800px' }}>
            This heatmap visualizes the relationship between portfolio risk (volatility) and expected return, 
            helping identify optimal investment combinations. The color gradient represents the desirability score, 
            which combines three key factors:
          </p>
          <ul style={{ color: '#666', marginBottom: '1rem', maxWidth: '800px' }}>
            <li><strong>Return Score (40%):</strong> Higher returns are preferred, normalized within the range.</li>
            <li><strong>Risk Score (30%):</strong> Lower volatility is preferred, encouraging risk-adjusted decisions.</li>
            <li><strong>Sharpe Ratio (30%):</strong> Measures excess return per unit of risk, using a 2% risk-free rate.</li>
          </ul>
          <p style={{ color: '#666', marginBottom: '1rem', maxWidth: '800px' }}>
            The current portfolio position (marked with a dot) shows where your allocation sits in this risk-return space. 
            This visualization helps identify whether your portfolio achieves an optimal balance between risk and return 
            based on your investment goals.
          </p>
        </div>
        <button
          onClick={() => setShowTable(!showTable)}
          style={{
            padding: '8px 16px',
            backgroundColor: showTable ? '#1565c0' : '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          {showTable ? 'Hide Data' : 'Show Data'}
        </button>
      </div>
      <svg width={width} height={height} style={{ backgroundColor: 'white' }}>
        {/* Grid cells */}
        {heatmapData.map(({ x, y, value }, i) => (
          <rect
            key={i}
            x={xScale(x)}
            y={yScale(y)}
            width={cellSize.width}
            height={cellSize.height}
            fill={getColor(value)}
            opacity={0.8}
          />
        ))}

        {/* Axes */}
        <line
          x1={padding.left}
          y1={height - padding.bottom}
          x2={width - padding.right}
          y2={height - padding.bottom}
          stroke="#666"
          strokeWidth={1}
        />
        <line
          x1={padding.left}
          y1={padding.top}
          x2={padding.left}
          y2={height - padding.bottom}
          stroke="#666"
          strokeWidth={1}
        />

        {/* X-axis labels */}
        {Array.from({ length: 5 }, (_, i) => {
          const value = riskRange.min + (i * (riskRange.max - riskRange.min) / 4);
          return (
            <g key={i} transform={`translate(${xScale(value)},${height - padding.bottom + 20})`}>
              <text
                textAnchor="middle"
                fill="#666"
                fontSize={12}
              >
                {(value * 100).toFixed(1)}%
              </text>
            </g>
          );
        })}
        <text
          x={width / 2}
          y={height - 10}
          textAnchor="middle"
          fill="#666"
          fontSize={12}
        >
          Risk (Volatility)
        </text>

        {/* Y-axis labels */}
        {Array.from({ length: 5 }, (_, i) => {
          const value = returnRange.min + (i * (returnRange.max - returnRange.min) / 4);
          return (
            <g key={i} transform={`translate(${padding.left - 10},${yScale(value)})`}>
              <text
                textAnchor="end"
                fill="#666"
                fontSize={12}
                dominantBaseline="middle"
              >
                {(value * 100).toFixed(1)}%
              </text>
            </g>
          );
        })}
        <text
          transform={`translate(${20},${height / 2}) rotate(-90)`}
          textAnchor="middle"
          fill="#666"
          fontSize={12}
        >
          Expected Return
        </text>

        {/* Current portfolio position */}
        <circle
          cx={xScale(currentPosition.risk)}
          cy={yScale(currentPosition.return)}
          r={6}
          fill="white"
          stroke="#1976d2"
          strokeWidth={2}
        />
        <circle
          cx={xScale(currentPosition.risk)}
          cy={yScale(currentPosition.return)}
          r={3}
          fill="#1976d2"
        />

        {/* Region labels */}
        <g transform={`translate(${xScale(0.05)},${yScale(0.12)})`}>
          <text fill="#666" fontSize={12}>Conservative</text>
        </g>
        <g transform={`translate(${xScale(0.10)},${yScale(0.08)})`}>
          <text fill="#666" fontSize={12}>Moderate</text>
        </g>
        <g transform={`translate(${xScale(0.15)},${yScale(0.04)})`}>
          <text fill="#666" fontSize={12}>Aggressive</text>
        </g>
      </svg>

      {showTable && (
        <div style={{ marginTop: '2rem', overflowX: 'auto' }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            borderRadius: '4px'
          }}>
            <thead>
              <tr>
                <th style={{ 
                  padding: '12px', 
                  textAlign: 'left', 
                  borderBottom: '2px solid #eee',
                  color: '#666'
                }}>
                  Risk (Volatility)
                </th>
                <th style={{ 
                  padding: '12px', 
                  textAlign: 'right', 
                  borderBottom: '2px solid #eee',
                  color: '#666'
                }}>
                  Return
                </th>
                <th style={{ 
                  padding: '12px', 
                  textAlign: 'right', 
                  borderBottom: '2px solid #eee',
                  color: '#666'
                }}>
                  Desirability Score
                </th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, index) => (
                <tr key={index}>
                  <td style={{ 
                    padding: '12px',
                    borderBottom: '1px solid #eee',
                    color: '#666'
                  }}>
                    {(row.risk * 100).toFixed(1)}%
                  </td>
                  <td style={{ 
                    padding: '12px',
                    borderBottom: '1px solid #eee',
                    textAlign: 'right',
                    color: '#666'
                  }}>
                    {(row.return * 100).toFixed(1)}%
                  </td>
                  <td style={{ 
                    padding: '12px',
                    borderBottom: '1px solid #eee',
                    textAlign: 'right',
                    color: '#666'
                  }}>
                    {row.score.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RiskReturnHeatmap;
